import { type UseFormReturn, type FieldArrayWithId } from 'react-hook-form'
import { Plus } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/common/ui/tabs'
import StageForm from '@/modules/messaging/pages/CreateBrokeFlexMessagePage/components/StageForm'
import type { CampaignFormValues } from '@/modules/messaging/pages/CreateBrokeFlexMessagePage/types'
import type { BrokeFlexMessageCampaignModel } from '@/modules/messaging/types'

interface StagesCardProps {
	form: UseFormReturn<CampaignFormValues>
	stageFields: FieldArrayWithId<CampaignFormValues, 'stages', 'id'>[]
	activeTab: string
	onActiveTabChange: (value: string) => void
	onAddStage: () => void
	onRemoveStage: (index: number) => void
	onMoveStage: (from: number, to: number) => void
	onPreview: (stageId: string) => void
	mode: 'create' | 'edit'
	campaignId?: string
	initialData?: BrokeFlexMessageCampaignModel
}

export default function StagesCard({
	form,
	stageFields,
	activeTab,
	onActiveTabChange,
	onAddStage,
	onRemoveStage,
	onPreview,
	mode,
	campaignId,
	initialData,
}: StagesCardProps) {
	return (
		<Card>
			<CardHeader>
				<div className="flex items-center justify-between">
					<div>
						<CardTitle>ระยะการส่งข้อความ</CardTitle>
						<p className="mt-2 text-sm text-muted-foreground">
							แต่ละระยะคือข้อความที่จะส่งหลังจากผู้เล่นเงินหมดและยังไม่ฝากเงินเพิ่ม
						</p>
					</div>
					<Button
						type="button"
						onClick={onAddStage}
						size="sm"
						disabled={stageFields.length >= 4}
					>
						<Plus className="mr-2 h-4 w-4" />
						เพิ่มระยะ
					</Button>
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				{stageFields.length === 0 ? (
					<div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-8 text-center">
						<p className="text-muted-foreground">
							ยังไม่มีระยะการส่ง กดปุ่ม &quot;เพิ่มระยะ&quot; เพื่อเริ่มต้น
						</p>
					</div>
				) : (
					<Tabs value={activeTab} onValueChange={onActiveTabChange}>
						<TabsList className="w-full justify-start">
							{stageFields.map((stage, stageIndex) => (
								<TabsTrigger
									key={stage.id}
									value={String(stageIndex)}
									className="flex-1"
								>
									ระยะที่ {stageIndex + 1}
									<span className="ml-2 text-xs text-muted-foreground">
										(
										{form.watch(`stages.${stageIndex}.minutesAfterTrigger`) ||
											0}{' '}
										นาที)
									</span>
								</TabsTrigger>
							))}
						</TabsList>
						{stageFields.map((stage, stageIndex) => {
							const stageId =
								mode === 'edit' &&
								initialData?.brokeNotificationSettings?.stages?.[stageIndex]?.id
									? initialData.brokeNotificationSettings.stages[stageIndex].id
									: undefined

							return (
								<TabsContent key={stage.id} value={String(stageIndex)}>
									<StageForm
										form={form}
										stageIndex={stageIndex}
										stageId={stageId}
										campaignId={campaignId}
										mode={mode}
										onPreview={stageId ? onPreview : undefined}
										onRemove={() => {
											onRemoveStage(stageIndex)
											if (activeTab === String(stageIndex)) {
												onActiveTabChange('0')
											}
										}}
										canRemove={stageFields.length > 1}
									/>
								</TabsContent>
							)
						})}
					</Tabs>
				)}
			</CardContent>
		</Card>
	)
}
